import React, { useEffect, useState } from 'react';

import { Trans } from 'react-i18next';

import useForm from 'components/hooks/useForm';
import useRequestError from 'components/hooks/useRequestError';
import useSidePanel from 'components/hooks/useSidePanel';

import SuccessToast from 'components/application/SuccessToast';

import { useProjectCoshhContext } from 'components/contexts/ProjectCoshhContext';
import { useBreadBoard } from 'components/contexts/Toaster';
import ProjectCoshhActionsBar from 'components/projectCoshh/components/ProjectCoshhActionsBar';
import ProjectCoshhBlankNotice from 'components/projectCoshh/components/ProjectCoshhBlankNotice';
import ProjectCoshhDestroyModal from 'components/projectCoshh/components/ProjectCoshhDestroyModal';
import ProjectCoshhDownloadModal from 'components/projectCoshh/components/ProjectCoshhDownloadModal';
import ProjectCoshhSidePanel from 'components/projectCoshh/components/ProjectCoshhSidePanel';
import ProjectCoshhTable from 'components/projectCoshh/components/ProjectCoshhTable';

import {
  defaultCoshhDocument,
  formatPersonsAtRiskValues,
  updatedPersonsAtRiskValues,
} from 'components/helpers/resources/coshhDocuments';

export default function ProjectCoshhTab() {
  const breadBoard = useBreadBoard();

  const [
    currentCoshhDocument,
    setCurrentCoshhDocument,
    handleCoshhInputChange,
    handleCoshhOptionChange,
    ,
    ,
    handleOptionSelect,
  ] = useForm(defaultCoshhDocument);

  const { comparisonValueForCustomValueUsage, coshhDocumentPolicy, projectId } =
    useProjectCoshhContext();

  const [
    requestError,
    submitDisabled,
    removeErrorStyling,
    resetRequestError,
    handleRequestError,
  ] = useRequestError();

  const [coshhDocuments, setCoshhDocuments] = useState({
    data: [],
    included: [],
  });

  const [initialPersonsAtRisk, setInitialPersonsAtRisk] = useState([]);

  const [
    isSidePanelOpen,
    setIsSidePanelOpen,
    _openSidePanel,
    closeSidePanel,
    _resetSidePanelContext,
    sidePanelContext,
    setSidePanelContext,
  ] = useSidePanel(false, 'show');

  const [isLoaded, setIsLoaded] = useState(false);

  const [isDestroyModalOpen, setIsDestroyModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  const [isSidePanelSubmitDisabled, setIsSidePanelSubmitDisabled] =
    useState(false);

  const coshhDocumentUpdateParams = () => {
    const updatedPersonsAtRisk = updatedPersonsAtRiskValues(
      initialPersonsAtRisk,
      currentCoshhDocument.personsAtRisk,
    );

    return {
      quantity_on_site: currentCoshhDocument.quantityOnSite,
      quantity_to_be_used: currentCoshhDocument.quantityToBeUsed,
      substance_quantity_per_day: currentCoshhDocument.substanceQuantityPerDay,
      uses: currentCoshhDocument.uses,
      method_of_use_other:
        currentCoshhDocument.uses === comparisonValueForCustomValueUsage ?
          currentCoshhDocument.methodOfUseOther
        : '',
      staff_number: currentCoshhDocument.staffNumber,
      worker_exposure_time: currentCoshhDocument.workerExposureTime,
      worker_exposure_time_other:
        (
          currentCoshhDocument.workerExposureTime ===
          comparisonValueForCustomValueUsage
        ) ?
          currentCoshhDocument.workerExposureTimeOther
        : '',
      exposure_frequency: currentCoshhDocument.exposureFrequency,
      exposure_frequency_other:
        (
          currentCoshhDocument.exposureFrequency ===
          comparisonValueForCustomValueUsage
        ) ?
          currentCoshhDocument.exposureFrequencyOther
        : '',
      location: currentCoshhDocument.location,
      level_of_risk: currentCoshhDocument.levelOfRisk,
      additional_information: currentCoshhDocument.additionalInformation,
      assigned_persons_at_risks_attributes: updatedPersonsAtRisk,
      persons_at_risk_other:
        (
          updatedPersonsAtRisk.find(
            (personAtRisk) =>
              personAtRisk.name === comparisonValueForCustomValueUsage &&
              personAtRisk._destroy === '1',
          )
        ) ?
          ''
        : currentCoshhDocument.personsAtRiskOther,
    };
  };

  const defaultCoshhDocumentRowData = (
    coshhDocument,
    formattedPersonsAtRisk,
  ) => {
    const { attributes, id } = coshhDocument;

    const coshhAttributes = Object.entries(attributes).reduce(
      (accumulator, [key, value]) => {
        accumulator[key] = value || '';
        return accumulator;
      },
      {},
    );

    return {
      ...coshhAttributes,
      id,
      personsAtRisk: formattedPersonsAtRisk,
    };
  };

  const fetchCoshhDocuments = () => {
    axios
      .get(`/projects/${projectId}/coshh.json`)
      .then((response) => {
        setCoshhDocuments(response.data);
        setIsLoaded(true);
        setIsSidePanelSubmitDisabled(false);
      })
      .catch(() => breadBoard.addInedibleToast());
  };

  useEffect(() => {
    fetchCoshhDocuments();
  }, []);

  const deleteCoshhDocument = () => {
    axios
      .delete(`/projects/${projectId}/coshh/${currentCoshhDocument.id}`)
      .then(() => {
        setIsSidePanelOpen(false);
        fetchCoshhDocuments();

        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey='projectCoshh.components.ProjectCoshhTab.deleteCoshhDocument.success'
                ns='components'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{ name: currentCoshhDocument.title }}
              />
            }
            onBurnToast={breadBoard.handleBurnToast}
          />,
        );

        setCurrentCoshhDocument(defaultCoshhDocument);
      })
      .catch(() => breadBoard.addInedibleToast());
  };

  const handleEditCoshhDocumentSubmit = () => {
    setIsSidePanelSubmitDisabled(true);

    axios
      .patch(`/projects/${projectId}/coshh/${currentCoshhDocument.id}`, {
        coshh_document: coshhDocumentUpdateParams(),
      })
      .then(() => {
        setIsSidePanelOpen(false);
        fetchCoshhDocuments();
        resetRequestError();

        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey='projectCoshh.components.ProjectCoshhTab.updateCoshhDocument.success'
                ns='components'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{ name: currentCoshhDocument.title }}
              />
            }
            onBurnToast={breadBoard.handleBurnToast}
          />,
        );

        setCurrentCoshhDocument(defaultCoshhDocument);
      })
      .catch(handleRequestError);
  };

  const handleCoshhDocumentRowClick = (coshhDocument) => {
    const personsAtRisk =
      coshhDocuments.included?.filter(
        (includedResource) =>
          includedResource.relationships.personable.data.id ===
            coshhDocument.id &&
          includedResource.type === 'assignedPersonsAtRisk',
      ) || [];

    const formattedPersonsAtRisk = formatPersonsAtRiskValues(personsAtRisk);

    setInitialPersonsAtRisk(formattedPersonsAtRisk);
    setCurrentCoshhDocument(
      defaultCoshhDocumentRowData(coshhDocument, formattedPersonsAtRisk),
    );
    setSidePanelContext('show');
    setIsSidePanelOpen(true);
  };

  const handleDownloadClick = ({ coshhDocument }) => {
    setCurrentCoshhDocument({
      ...defaultCoshhDocument,
      id: coshhDocument.id,
      title: coshhDocument.attributes.title,
    });

    setIsDownloadModalOpen(true);
  };

  const handleDeleteClick = ({ coshhDocument }) => {
    setCurrentCoshhDocument({
      ...defaultCoshhDocument,
      id: coshhDocument.id,
      title: coshhDocument.attributes.title,
    });

    setIsDestroyModalOpen(true);
  };

  const handleSidePanelClose = () => {
    resetRequestError();
    closeSidePanel();
  };

  useEffect(() => {
    if (requestError) setIsSidePanelSubmitDisabled(false);
  }, [requestError]);

  return (
    isLoaded && (
      <>
        {coshhDocumentPolicy.canCreate && <ProjectCoshhActionsBar />}
        {coshhDocuments.data.length > 0 ?
          <ProjectCoshhTable
            coshhDocuments={coshhDocuments.data}
            onCoshhDocumentRowClick={handleCoshhDocumentRowClick}
            onDeleteClick={handleDeleteClick}
            onDownloadClick={handleDownloadClick}
          />
        : <ProjectCoshhBlankNotice />}
        {isDestroyModalOpen && currentCoshhDocument.id && (
          <ProjectCoshhDestroyModal
            currentCoshhDocument={currentCoshhDocument}
            onClose={() => setIsDestroyModalOpen(false)}
            onDelete={() => {
              setIsDestroyModalOpen(false);
              deleteCoshhDocument();
            }}
          />
        )}
        <ProjectCoshhDownloadModal
          currentCoshhDocument={currentCoshhDocument}
          isOpen={isDownloadModalOpen}
          onClose={() => setIsDownloadModalOpen(false)}
        />
        <ProjectCoshhSidePanel
          coshhDocument={currentCoshhDocument}
          isOpen={isSidePanelOpen}
          isSubmitDisabled={submitDisabled || isSidePanelSubmitDisabled}
          onClose={handleSidePanelClose}
          onCoshhInputChange={handleCoshhInputChange}
          onCoshhOptionChange={handleCoshhOptionChange}
          onDeleteProjectCoshhClick={() => setIsDestroyModalOpen(true)}
          onEditCoshhDocumentSubmit={handleEditCoshhDocumentSubmit}
          onOptionSelect={handleOptionSelect}
          removeErrorStyling={removeErrorStyling}
          requestError={requestError}
          setSidePanelContext={setSidePanelContext}
          sidePanelContext={sidePanelContext}
        />
      </>
    )
  );
}
